import type { FC, FormEventHandler, KeyboardEvent, MouseEvent } from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { validationEmail, validationPhone } from '@utils/validation';
import API from '@api';
import './index.scss';
import { type FormStage } from '@components/AuthForm';
import type { AvailableAuthMethods } from '@components/AuthForm/authProviders';
import { observer } from 'mobx-react';
import BarMenuMain from './BarMenuMain';
import Main from './Main';
import type { CartStoreType } from '@store/CartStore';

interface AuthFormMainProps {
  setUserExists: (value: boolean) => void;
  userExists: boolean;
  getUserCredentials: (data: { field: string; value: string }) => void;
  setFormStage: (value: FormStage) => void;
  authViaSms: (data: { phone: string; code?: number }) => Promise<void>;
  authViaCall: (data: { phone: string; code?: number }) => Promise<void>;
  authViaLoginByPhoneAnyWay?: (data: {
    phone: string;
  }) => Promise<{ peopleExist: boolean; isLoggedIn: boolean } | null>;
  barMenuRequestCall?: (data: { phone: string }) => Promise<void>;
  authViaTg?: (data: { phone: string; code?: number }) => void;
  onMaxClick?: () => void;
  loginErrorMsg: string;
  onLoginOrRegisterError: string;
  setOnLoginOrRegisterError: (value: string) => void;
  isShowTest: boolean;
  setIsShowCAPTCHA: (value: boolean) => void;
  cartStore: CartStoreType;
  isBarMenu?: boolean;
  authMethods: AvailableAuthMethods;
  reinitializeTinkoff: () => void;
  compact?: boolean;
  initialPhone?: string;
}

const AuthFormMain: FC<AuthFormMainProps> = ({
  isBarMenu,
  setUserExists,
  userExists,
  getUserCredentials,
  setFormStage,
  authViaSms,
  authViaCall,
  authViaTg,
  onMaxClick,
  authViaLoginByPhoneAnyWay,
  barMenuRequestCall,
  loginErrorMsg,
  onLoginOrRegisterError,
  setOnLoginOrRegisterError,
  isShowTest,
  setIsShowCAPTCHA,
  cartStore,
  authMethods,
  reinitializeTinkoff,
  compact,
  initialPhone
}) => {
  const [formData, setFormData] = useState<{
    text: { value: string; error?: string };
  }>({
    text: {
      value:
        initialPhone && validationPhone(initialPhone, true) ? initialPhone : '',
      error: ''
    }
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [tabValues, setTabValues] = useState<Record<number, string>>({
    0: '',
    1: '',
    2: ''
  });
  const clickLockRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      // const input = document.querySelector(
      //   '.tinkoff-input'
      // ) as HTMLInputElement;
      const input = document.querySelector(
        '.AuthForm .InputWrapper__input'
      ) as HTMLInputElement | null;
      if (input) {
        input.focus();
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [currentTab]);

  const setInputData = useCallback(
    ({ value, error }: { value: string; error?: string }): void => {
      setFormData(prev => ({ ...prev, text: { value, error } }));
      if (currentTab === 0 || currentTab === 1) {
        setTabValues(prev => ({ ...prev, 0: value, 1: value }));
      } else {
        setTabValues(prev => ({ ...prev, [currentTab]: value }));
      }
    },
    [currentTab]
  );

  useEffect(() => {
    setIsShowCAPTCHA(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userExists) {
      setUserExists(true);
    }
    if (onLoginOrRegisterError) {
      setOnLoginOrRegisterError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.text.value]);

  useEffect(() => {
    if (initialPhone) {
      return;
    }
    const { phone } = cartStore.cart;

    if ((currentTab === 0 || currentTab === 1) && phone) {
      setFormData(prev => ({ ...prev, text: { value: phone, error: '' } }));
      setTabValues(prev => ({ ...prev, 0: phone, 1: phone }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const setInputError = useCallback(error => {
    setFormData(prev => ({ ...prev, text: { ...prev.text, error } }));
  }, []);

  const onBtnClickHandler = useCallback(
    (e: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const inputValue = formData.text.value.trim();
      const isInputValidEmail = validationEmail(inputValue);
      const isInputValidPhone = validationPhone(inputValue, true);
      if ((currentTab === 0 || currentTab === 1) && isInputValidPhone) {
        if (clickLockRef.current) {
          return;
        }
        clickLockRef.current = true;

        const userPhone = inputValue.replace(/\D/g, '');
        const russianFormatPhone =
          userPhone.length === 10 ? '7' + userPhone : userPhone;
        setInputData({ value: russianFormatPhone, error: '' });
        getUserCredentials({ field: 'phone', value: russianFormatPhone });

        if (isBarMenu && authViaLoginByPhoneAnyWay && barMenuRequestCall) {
          authViaLoginByPhoneAnyWay({ phone: russianFormatPhone })
            .then(async result => {
              if (result && !result.isLoggedIn) {
                await barMenuRequestCall({ phone: russianFormatPhone });
              }
            })
            .finally(() => {
              clickLockRef.current = false;
            });
          return;
        }

        if (currentTab === 0) {
          authViaSms({ phone: russianFormatPhone }).finally(() => {
            clickLockRef.current = false;
          });
        } else {
          authViaCall({ phone: russianFormatPhone }).finally(() => {
            clickLockRef.current = false;
          });
        }
        return;
      }

      if (currentTab === 2 && isInputValidEmail) {
        API.checkPeopleExist({ email: inputValue })
          .then(res => {
            getUserCredentials({ field: 'email', value: inputValue });
            if (res.checkPeopleExist) {
              setFormStage('email-login');
            } else {
              setUserExists(false);
              setInputError('Аккаунт не существует');
            }
          })
          .catch(err => {
            console.log('Error: ', err);
          });
        return;
      }

      if ((currentTab === 0 || currentTab === 1) && !isInputValidPhone) {
        setInputError('Телефон введен некорректно');
      } else if (currentTab === 2 && !isInputValidEmail) {
        setInputError('Email введен некорректно');
      }
    },
    [
      authViaSms,
      authViaCall,
      authViaLoginByPhoneAnyWay,
      barMenuRequestCall,
      formData.text.value,
      getUserCredentials,
      setFormStage,
      setInputData,
      setInputError,
      setUserExists,
      currentTab,
      isBarMenu
    ]
  );
  const onRegisterByEmailClickHandler = (): void => {
    setFormStage('email-login');
  };

  const onTelegramClick = useCallback(async () => {
    const inputValue = formData.text.value.trim();
    const isInputValidPhone = validationPhone(inputValue, true);
    if (!isInputValidPhone) {
      setInputError('Телефон введен некорректно');
      return;
    }
    const userPhone = inputValue.replace(/\D/g, '');
    const russianFormatPhone =
      userPhone.length === 10 ? '7' + userPhone : userPhone;
    setInputData({ value: russianFormatPhone, error: '' });
    getUserCredentials({ field: 'phone', value: russianFormatPhone });

    if (!isBarMenu) {
      if (clickLockRef.current) {
        return;
      }
      clickLockRef.current = true;
      Promise.resolve(authViaTg?.({ phone: russianFormatPhone })).finally(
        () => {
          clickLockRef.current = false;
        }
      );
      return;
    }

    try {
      if (authViaLoginByPhoneAnyWay) {
        const result = await authViaLoginByPhoneAnyWay({
          phone: russianFormatPhone
        });

        if (result?.peopleExist && !result.isLoggedIn && authViaTg) {
          authViaTg({ phone: russianFormatPhone });
          setFormStage('tg-login');
        }
      }
    } catch (error) {
      console.log(error);
      setInputError('Произошла ошибка при авторизации');
    }
  }, [
    formData.text.value,
    setInputError,
    setInputData,
    getUserCredentials,
    authViaLoginByPhoneAnyWay,
    authViaTg,
    setFormStage,
    isBarMenu
  ]);

  const btnTitle = !userExists
    ? 'Регистрация'
    : currentTab === 0
    ? 'Код по СМС'
    : 'Далее';

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        onBtnClickHandler(e);
      }
    },
    [onBtnClickHandler]
  );

  const changeTab = useCallback(
    tab => {
      setIsShowCAPTCHA(tab === 2);
      setCurrentTab(tab);
      const restoredValue = tabValues[tab] || '';
      setFormData(prev => ({
        ...prev,
        text: { value: restoredValue, error: '' }
      }));
      if (tab === 0 || tab === 1) {
        setUserExists(true);
      }
      if (tab === 0) {
        setTimeout(() => {
          reinitializeTinkoff();
        }, 100);
      }
    },
    [setIsShowCAPTCHA, tabValues, setUserExists, reinitializeTinkoff]
  );

  if (isBarMenu) {
    return (
      <BarMenuMain
        compact={compact}
        setInputData={setInputData}
        handleKeyDown={handleKeyDown}
        onSubmit={onSubmit}
        formData={formData}
        onRegisterByEmailClickHandler={onRegisterByEmailClickHandler}
        userExists={userExists}
        onTelegramClick={onTelegramClick}
        onMaxClick={onMaxClick}
        onBtnClickHandler={onBtnClickHandler}
        onLoginOrRegisterError={onLoginOrRegisterError}
      />
    );
  }

  return (
    <Main
      currentTab={currentTab}
      onSubmit={onSubmit}
      changeTab={changeTab}
      setInputData={setInputData}
      formData={formData}
      handleKeyDown={handleKeyDown}
      onRegisterByEmailClickHandler={onRegisterByEmailClickHandler}
      onBtnClickHandler={onBtnClickHandler}
      onTelegramClick={onTelegramClick}
      onMaxClick={onMaxClick}
      btnTitle={btnTitle}
      userExists={userExists}
      loginErrorMsg={loginErrorMsg}
      isShowTest={isShowTest}
      onLoginOrRegisterError={onLoginOrRegisterError}
      authMethods={authMethods}
      reinitializeTinkoff={reinitializeTinkoff}
    />
  );
};

export default observer(AuthFormMain);
